import * as React from 'react';
import { Row } from '@tanstack/react-table';

export interface DataTableRowStateValue {
    rowId: string;
    /** CONTRACT: the index into the FILTERED + SORTED row model, never the virtual-window index. */
    rowIndex: number;
    isSelected: boolean;
    isSelectable: boolean;
    /** Hover or focus-within. Always false unless the table sets `trackRowActivity`. */
    isActive: boolean;
    /** This row can expand. Always false unless the table sets `renderRowDetail`. */
    isExpandable: boolean;
    isExpanded: boolean;
    /** DOM id of the rendered region, or `null` while collapsed, so `aria-controls` is never dangling. */
    detailRegionId: string | null;
    /** This is the `summaryRow`, not a record: `rowIndex` is -1 and every other flag is false. */
    isSummary: boolean;
}

const RowStateContext = React.createContext<DataTableRowStateValue | null>(null);

export const DataTableRowStateProvider = RowStateContext.Provider;

/**
 * Resets the row context for a table's own subtree. A nested `DataTable` renders inside the outer row's
 * provider, so without this its header and state rows would read the OUTER row's state; body cells
 * re-establish their own value, and the `useDataTableRow` throw comes back for everything else.
 */
export const DataTableRowStateReset: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <RowStateContext.Provider value={null}>{children}</RowStateContext.Provider>
);

/**
 * Row state for cells in their own component. Inline `cell: ({ row }) => ...` arrows must use
 * `<DataTableRowState>` instead: react-hooks resolves an arrow assigned to a property named `cell` to that
 * name, making any hook call inside it a hard `rules-of-hooks` error.
 */
export const useDataTableRow = (): DataTableRowStateValue => {
    const value = React.useContext(RowStateContext);

    if (value === null) {
        throw new Error('useDataTableRow() must be called from inside a DataTable body cell.');
    }

    return value;
};

export interface DataTableRowStateProps {
    children: (row: DataTableRowStateValue) => React.ReactNode;
}

/** Render-prop form of {@link useDataTableRow}, for inline `cell:` arrows. */
export const DataTableRowState: React.FC<DataTableRowStateProps> = ({ children }) => {
    const rowState = useDataTableRow();

    return <>{children(rowState)}</>;
};

/** id -> filtered-and-sorted index, for imperative look-ups off the table handle. */
export const useRowIndexMap = <TData,>(rows: Row<TData>[]): Map<string, number> =>
    React.useMemo(() => new Map<string, number>(rows.map((row, index): [string, number] => [row.id, index])), [rows]);
